import { useCallback, useState, ReactNode, ComponentProps } from 'react';
import locale from '@glu/locale';
import {
  ArrowRefreshAshIcon,
  ColumnsAshIcon,
  FunnelAshIcon,
  PrinterAshIcon,
} from '@glu/icons-react';
import { AgGridReact } from 'ag-grid-react';
import classnames from 'classnames';
import {
  Export,
  IExportProps,
  ColumnFilterDrawer,
  Footer,
  ControlBar,
  Search,
  IColumnFilterDrawerProps,
} from '@glu/data-components';
import Filters, { IFiltersAgGridProps } from 'Filters/FiltersAgGrid';
import { useComposedGridContext } from 'ComposedGrid/ComposedGridContext';
import Columns, { IColumnsAgGridProps } from 'Columns/ColumnsAgGrid';
import Pagination, { TPaginationAgGridProps } from 'Pagination/PaginationAgGrid';
import FilterChips, { IFilterChipsAgGridProps } from 'FilterChips/FilterChipsAgGrid';
import SavedViews, { TSavedViewsAgGridProps } from 'SavedViews/SavedViewsAgGrid';
import BulkActions, { IBulkActionsAgGridProps } from 'BulkActions/BulkActionsAgGrid';
import printGrid from './utils/printGrid';
import exportGrid from './utils/exportGrid';
import searchGrid from './utils/searchGrid';
import refreshGrid from './utils/refreshGrid';
import useStyles from './ComposedViewAgGrid.styles';

export interface IComposedViewAgGridProps
  extends Pick<IColumnFilterDrawerProps, 'hideColumns' | 'hideFilters'> {
  /** The child to render around the composed view */
  children: ReactNode;
  /** Will hide pagination if true */
  hidePagination?: boolean;
  /** Will hide savedViews if true */
  hideSavedViews?: boolean;
  /** Will hide filterChips if true */
  hideFilterChips?: boolean;
  /** Will hide the print icon if true */
  hidePrint?: boolean;
  /** Will hide the search functionality if true */
  hideSearch?: boolean;
  /** Will hide the export icon if true */
  hideExport?: boolean;
  /** Will hide the refresh icon if true */
  hideRefresh?: boolean;
  /** SavedView options */
  savedViewsOptions?: TSavedViewsAgGridProps;
  /** Column drawer options */
  columnsOptions?: Omit<IColumnsAgGridProps, 'onClose'>;
  /** Pagination options */
  paginationOptions?: TPaginationAgGridProps;
  /** BulkActions options, bulk actions will be hidden if not passed */
  bulkActionsOptions?: IBulkActionsAgGridProps;
  /** Filter options */
  filterOptions?: Omit<IFiltersAgGridProps, 'onClose'>;
  /** options for the control bar */
  controlBarOptions?: {
    /** Callback used to export the grid */
    onExport?(grid: AgGridReact): void;
    /** Callback used to print the grid. The grid by default will go into print layout for 2 seconds */
    onPrint?(grid: AgGridReact): void;
    /** Callback used to refresh the grid, if not passed the refresh icon will be hidden */
    onRefresh?(grid: AgGridReact): void;
    /** Callback used search the grid. By default performs a quick search */
    onSearch?(grid: AgGridReact, filterString: string): void;
    /** Menu items to show on the export icon */
    exportMenuItems?: IExportProps['menuItems'];
  };
  filterChipsOptions?: IFilterChipsAgGridProps;
  footerSticky?: ComponentProps<typeof Footer>['sticky'];
}

const emptyObj = {};

const ComposedViewAgGrid = ({
  bulkActionsOptions,
  children,
  columnsOptions,
  controlBarOptions: {
    exportMenuItems,
    onExport = exportGrid,
    onPrint = printGrid,
    onRefresh = refreshGrid,
    onSearch = searchGrid,
  } = emptyObj,
  filterChipsOptions,
  filterOptions,
  footerSticky,
  hideColumns,
  hideExport,
  hideFilterChips,
  hideFilters,
  hidePagination,
  hidePrint,
  hideRefresh,
  hideSavedViews,
  hideSearch,
  paginationOptions,
  savedViewsOptions,
}: IComposedViewAgGridProps) => {
  const [drawerTab, setDrawerTab] = useState<'columns' | 'filters' | undefined>();
  const { gridRef, ready } = useComposedGridContext();
  const classes = useStyles();

  const onCloseDrawer = useCallback(() => setDrawerTab(undefined), []);

  const gridSearch = useCallback(
    (filterString: string) => onSearch(gridRef.current, filterString),
    [gridRef, onSearch]
  );

  const onGridPrint = useCallback(() => onPrint(gridRef.current), [gridRef, onPrint]);

  const onGridExport = useCallback(() => onExport(gridRef.current), [onExport, gridRef]);

  const onGridRefresh = useCallback(() => onRefresh(gridRef.current), [onRefresh, gridRef]);

  return (
    <div>
      <ControlBar
        leftControls={
          ready && (
            <div>
              {/* eslint-disable-next-line react/jsx-props-no-spreading */}
              {!hideSavedViews && <SavedViews {...savedViewsOptions} />}
              {/* eslint-disable-next-line react/jsx-props-no-spreading */}
              {!hideFilterChips && <FilterChips {...filterChipsOptions} />}
            </div>
          )
        }
        rightControls={
          <>
            {!hideSearch && <Search onSearchSubmit={gridSearch} />}
            {!hideFilters && (
              <FunnelAshIcon
                actionable={{
                  'aria-label': locale.get('gridReact.filters'),
                  title: locale.get('gridReact.filters'),
                }}
                aria-label={locale.get('gridReact.filters')}
                data-qa="filterToggle"
                onClick={() => {
                  setDrawerTab('filters');
                }}
                title={locale.get('gridReact.filters')}
              />
            )}
            {!hideColumns && (
              <ColumnsAshIcon
                actionable={{
                  'aria-label': locale.get('gridReact.columns'),
                  title: locale.get('gridReact.columns'),
                }}
                data-qa="columnsToggle"
                onClick={() => {
                  setDrawerTab('columns');
                }}
                title={locale.get('gridReact.columns')}
              />
            )}
            {!hideRefresh && (
              <div
                className={classnames({
                  [classes.auxWrap]: !hideSearch || !hideFilters || !hideColumns,
                })}
              >
                <ArrowRefreshAshIcon
                  actionable={{
                    'aria-label': locale.get('gridReact.refresh'),
                    title: locale.get('gridReact.refresh'),
                  }}
                  onClick={onGridRefresh}
                  title={locale.get('gridReact.refresh')}
                />
              </div>
            )}
            {(!hidePrint || !hideExport || exportMenuItems) && (
              <div
                className={classnames({
                  [classes.auxWrap]: !hideSearch || !hideFilters || !hideColumns,
                })}
              >
                {!hidePrint && (
                  <PrinterAshIcon
                    actionable={{
                      'aria-label': locale.get('gridReact.print'),
                      title: locale.get('gridReact.print'),
                    }}
                    data-qa="printButton"
                    onClick={onGridPrint}
                    title={locale.get('gridReact.print')}
                  />
                )}
                {!hideExport && <Export menuItems={exportMenuItems} onClick={onGridExport} />}
              </div>
            )}
          </>
        }
      />
      {children}
      {(!hidePagination || bulkActionsOptions) && ready && (
        <Footer sticky={footerSticky}>
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          {bulkActionsOptions && <BulkActions {...bulkActionsOptions} />}
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          {!hidePagination && <Pagination {...paginationOptions} />}
        </Footer>
      )}
      {(!hideColumns || !hideFilters) && (
        <ColumnFilterDrawer
          activeTab={drawerTab}
          hideColumns={hideColumns}
          hideFilters={hideFilters}
          onChangeActiveTab={setDrawerTab}
          onClose={onCloseDrawer}
          open={!!drawerTab}
        >
          {drawerTab === 'columns' && !hideColumns && (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <Columns {...columnsOptions} onClose={onCloseDrawer} />
          )}
          {drawerTab === 'filters' && !hideFilters && (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <Filters {...filterOptions} onClose={onCloseDrawer} />
          )}
        </ColumnFilterDrawer>
      )}
    </div>
  );
};

export default ComposedViewAgGrid;
